import React, { useMemo, useState } from 'react';
import Modal from '../shared/Modal';
import { SelectField, SegmentedField, TextField, TextareaField } from '../shared/FormField';
import { GEOGRAPHIC_SCOPES, SOURCE_ORGANISATIONS } from '../data/enums';
import { useLocationOptions } from '../shared/useLocationOptions';

const PERIOD_OPTIONS = Array.from({ length: 7 }, (_, i) =>
  ['Q1', 'Q2', 'Q3', 'Q4'].map((q) => ({ value: `${2020 + i}-${q}`, label: `${2020 + i}-${q}` }))
).flat();

const blank = {
  dataset_name: '',
  source_organisation: '',
  indicator_reference: '',
  reference_period: '',
  geographic_scope: '',
  state: '',
  lga: '',
  value_numeric: '',
  notes: '',
};

const parseError = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'Unable to create dataset.';

export default function CreateSupplementaryDatasetModal({
  isOpen,
  onClose,
  onCreate,
  dataSources = [],
  indicatorOptions = [],
}) {
  const [form, setForm] = useState(blank);
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { stateOptions, lgaOptions, loadingLgas } = useLocationOptions(form.state);

  // Fall back to static SOURCE_ORGANISATIONS if the backend endpoint returned nothing
  const sourceOptions = useMemo(
    () => (dataSources.length > 0 ? dataSources : SOURCE_ORGANISATIONS),
    [dataSources]
  );

  const set = (key) => (value) => setForm((current) => {
    if (key === 'geographic_scope') {
      const next = { ...current, geographic_scope: value };
      if (String(value).toLowerCase() !== 'state') {
        next.state = '';
        next.lga = '';
      }
      return next;
    }

    if (key === 'state') {
      return { ...current, state: value, lga: '' };
    }

    return { ...current, [key]: value };
  });

  const submit = async () => {
    if (submitting) return;

    const numericValue =
      form.value_numeric === '' || form.value_numeric === null || form.value_numeric === undefined
        ? null
        : Number(form.value_numeric);

    if (
      !form.dataset_name.trim() ||
      !form.source_organisation ||
      !form.reference_period ||
      !form.geographic_scope ||
      numericValue === null ||
      Number.isNaN(numericValue)
    ) {
      setErrMsg('Please complete the required fields before saving.');
      setStatus('error');
      return;
    }

    if (
      String(form.geographic_scope).toLowerCase() === 'state' &&
      !String(form.state || '').trim()
    ) {
      setErrMsg('Please select a state for state-scoped datasets.');
      setStatus('error');
      return;
    }

    setSubmitting(true);
    setStatus('idle');
    setErrMsg('');

    try {
      await onCreate({
        dataset_name: form.dataset_name.trim(),
        source_organisation: form.source_organisation,
        indicator_reference: form.indicator_reference.trim() || null,
        reference_period: form.reference_period,
        geographic_scope: form.geographic_scope,
        state: form.state.trim() || null,
        lga: form.lga.trim() || null,
        value_numeric: numericValue,
        notes: form.notes.trim() || null,
      });
      setStatus('success');
    } catch (error) {
      setErrMsg(parseError(error));
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setForm(blank);
    setStatus('idle');
    setErrMsg('');
    onClose();
  };

  return (
    <Modal
      title="Create Supplementary Dataset"
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={submit}
      status={status}
      errorMessage={errMsg}
      onContinue={handleClose}
      submitLabel="Create"
      successTitle="Dataset Created"
      successMessage="The dataset was saved to the backend as a draft."
      isSubmitting={submitting}
      maxWidthClassName="max-w-[820px]"
      bodyGridClassName="grid gap-4 sm:grid-cols-2"
    >
      <TextField
        label="Dataset Name"
        required
        value={form.dataset_name}
        onChange={set('dataset_name')}
        placeholder="e.g. National GDP 2024"
      />

      <SelectField
        label="Source Organisation"
        required
        value={form.source_organisation}
        onChange={set('source_organisation')}
        options={sourceOptions}
        placeholder="Select data source"
      />

      {indicatorOptions.length > 0 ? (
        <SelectField
          label="Indicator Reference"
          value={form.indicator_reference}
          onChange={set('indicator_reference')}
          options={indicatorOptions}
          placeholder="Select indicator"
        />
      ) : (
        <TextField
          label="Indicator Reference"
          value={form.indicator_reference}
          onChange={set('indicator_reference')}
          placeholder="e.g. IMP-1"
        />
      )}

      <SelectField
        label="Reference Period"
        required
        value={form.reference_period}
        onChange={set('reference_period')}
        options={PERIOD_OPTIONS}
        placeholder="Select period"
      />

      <div className="sm:col-span-2">
        <SegmentedField
          label="Geographic Scope"
          required
          value={form.geographic_scope}
          onChange={set('geographic_scope')}
          options={GEOGRAPHIC_SCOPES}
        />
      </div>

      <SelectField
        label="State"
        value={form.state}
        onChange={set('state')}
        options={stateOptions}
        placeholder={form.geographic_scope === 'state' ? 'Select State' : 'State not needed'}
        disabled={form.geographic_scope !== 'state'}
        note="Required when geographic scope is State."
      />

      <SelectField
        label="LGA"
        value={form.lga}
        onChange={set('lga')}
        options={lgaOptions}
        placeholder={form.state ? (loadingLgas ? 'Loading LGAs...' : 'Select LGA') : 'Select State first'}
        disabled={form.geographic_scope !== 'state' || !form.state || loadingLgas}
        note="Optional for state-scoped records."
      />

      <TextField
        label="Value Numeric"
        required
        type="number"
        step="any"
        inputMode="decimal"
        value={form.value_numeric}
        onChange={set('value_numeric')}
        placeholder="Enter numeric value"
        note="This value is sent to the backend as a number."
      />

      <div className="sm:col-span-2">
        <TextareaField
          label="Notes"
          value={form.notes}
          onChange={set('notes')}
          placeholder="Optional notes about the source or calculation"
          rows={3}
        />
      </div>
    </Modal>
  );
}
